import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngxs/store';
import {
  DisableSettingsEdit,
  SettingsEditState,
  UnSavedSettingsEdit,
} from '@ops/services';
import { SessionManagerService, UsersService } from '@ops/shared-types';
import { Observable, Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'ops-security-settings',
  templateUrl: './ops-security-settings.component.html',
  styleUrl: './ops-security-settings.component.scss',
})
export class OpsSecuritySettingsComponent implements OnInit, OnDestroy {
  editState$: Observable<boolean> = inject(Store).select(
    SettingsEditState.getEditState
  );

  saveState$: Observable<boolean> = inject(Store).select(
    SettingsEditState.getSaveState
  );

  isPreview = true;

  oldPassword = '';
  newPassword = '';
  confirmedPassword = '';

  errorsTrack = {
    oldPassword: "Current password can't be empty",
    newPassword: "New password can't be empty",
    confirmedPassword: "Confirm password can't be empty",
  };

  passwordMissmatch = false;

  subs: Array<Subscription> = [];

  constructor(
    private store: Store,
    private sessionManager: SessionManagerService,
    private usersService: UsersService,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    const eState = this.editState$.subscribe((isEditing: any) => {
      this.isPreview = !isEditing;
      this.oldPassword = '';
      this.newPassword = '';
      this.confirmedPassword = '';
      this.errorsTrack = {
        oldPassword: "Current password can't be empty",
        newPassword: "New password can't be empty",
        confirmedPassword: "Confirm password can't be empty",
      };
    });

    const sState = this.saveState$.subscribe((saveChanges: any) => {
      if (saveChanges === true) {
        // console.log('SAVE CHANGES');

        const errorFields = Object.keys(this.errorsTrack);
        if (errorFields.length === 0 && this.passwordMissmatch === false) {
          this.saveNewPassword();
          this.store.dispatch(new UnSavedSettingsEdit());
        } else {
          const errorsList = errorFields.map((errorKey) => {
            return '\n' + errorKey + ': ' + this.errorsTrack[errorKey];
          });

          const missmatch = this.passwordMissmatch
            ? '\n' + this.translateService.instant('windowAlert.opsSecuritySettings.passwordMismatch')
            : '';

          const joinMessage = errorsList.join('') + missmatch;

          this.store.dispatch(new UnSavedSettingsEdit());

          window.alert(
            this.translateService.instant('windowAlert.opsSecuritySettings.invalidFields', { errors: joinMessage })
          );
        }
      }
    });

    this.subs.push(eState);
    this.subs.push(sState);
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
  }

  saveNewPassword() {
    const sub = this.usersService
      .changePassword(
        this.oldPassword,
        this.newPassword,
        this.confirmedPassword
      )
      .subscribe(
        (updateResp: any) => {
          // console.log('updateResp', updateResp);
          this.newPassword = '';
          this.oldPassword = '';
          this.confirmedPassword = '';
          this.store.dispatch(new DisableSettingsEdit());

          this.sessionManager.logout();
        },
        (error) => {
          if (error?.error?.message) {
            const errorsList = error?.error?.message.split('.');
            const fullMessage = this.translateService.instant('windowAlert.opsSecuritySettings.updateError', { errors: errorsList.join('.\n') });
            window.alert(fullMessage);
          } else if (error?.error) {
            const errorRespParsed = JSON.parse(error.error);
            const fullMessage = this.translateService.instant('windowAlert.opsSecuritySettings.updateError', { errors: errorRespParsed?.message });
            window.alert(fullMessage);
          }
        }
      );
    this.subs.push(sub);
  }

  onErrorHandler(error: string, field?: string) {
    if (!error || error === '') {
      delete this.errorsTrack[field];
    } else {
      this.errorsTrack[field] = error;
    }
  }

  validateMatch() {
    if (
      this.newPassword &&
      this.confirmedPassword &&
      this.newPassword.length >= 6 &&
      this.confirmedPassword.length >= 6
    ) {
      if (this.newPassword !== this.confirmedPassword) {
        this.passwordMissmatch = true;
      } else {
        this.passwordMissmatch = false;
      }
    } else {
      this.passwordMissmatch = false;
    }
  }

  onInputChange(newValue: string, field?: string): void {
    this[field] = newValue;

    this.validateMatch();
  }
}
